import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Trash2, User } from 'lucide-react';

/**
 * /app/admin/deletions — read-only audit view of account deletions.
 * Sources GET /admin/deletion-log (admin.user_delete + user.self_delete).
 */
export default function AdminDeletionLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    http.get('/admin/deletion-log')
      .then((r) => setRows(r.data?.deletions || []))
      .catch(() => setError(true));
  }, [user]);

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6" data-testid="admin-deletion-log">
      <Link to="/app/admin" data-testid="admin-deletions-back"
        className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to admin
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Admin</p>
        <h1 className="font-heading text-3xl mt-1">Account Deletions</h1>
        <p className="text-navy/50 text-sm mt-1">Every account removed — by an admin or self-service. Newest first.</p>
      </div>

      {error && (
        <div className="bg-white rounded-3xl p-6 card-soft text-navy/60" data-testid="admin-deletions-error">
          Couldn&apos;t load the deletion log.{' '}
          <button onClick={() => window.location.reload()} className="underline" data-testid="admin-deletions-retry">Retry</button>
        </div>
      )}

      {!error && rows === null && (
        <div className="text-navy/50" data-testid="admin-deletions-loading">Loading…</div>
      )}

      {!error && rows !== null && rows.length === 0 && (
        <div className="bg-white rounded-3xl p-10 card-soft text-center text-navy/50" data-testid="admin-deletions-empty">
          No account deletions recorded yet.
        </div>
      )}

      {!error && rows && rows.length > 0 && (
        <div className="bg-white rounded-3xl p-6 card-soft" data-testid="admin-deletions-list">
          <p className="font-heading text-lg mb-3">Recent deletions ({rows.length})</p>
          <div className="divide-y divide-navy/5">
            {rows.map((d, i) => (
              <div key={`${d.target_id}-${d.at}-${i}`}
                className="py-3 flex items-start justify-between gap-4"
                data-testid={`admin-deletion-row-${i}`}>
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${d.type === 'self' ? 'bg-navy/10 text-navy' : 'bg-gold/15 text-gold'}`}>
                    {d.type === 'self' ? <User size={15}/> : <Trash2 size={15}/>}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-navy">{d.target_email || d.target_id}</p>
                    <p className="text-xs text-navy/50 mt-0.5">
                      {d.type === 'self' ? 'Deleted their own account' : `Deleted by ${d.deleted_by || 'an admin'}`}
                      {d.target_role ? ` · ${d.target_role}` : ''}
                      {d.target_was_admin ? ' · was admin' : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.type === 'self' ? 'bg-navy text-white' : 'bg-gold text-white'}`}>
                    {d.type === 'self' ? 'Self' : 'Admin'}
                  </span>
                  <p className="text-xs text-navy/50 mt-1">{fmt(d.at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
